import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Fade,
  Tooltip,
  LinearProgress,
  Badge,
  AppBar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack
} from '@mui/material';
import {
  TrendingUp as StakingIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  AccountBalance as ValidatorIcon,
  Timeline as StatsIcon,
  Star as RewardsIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Remove as RemoveIcon,
  Security as SecurityIcon,
  VerifiedUser as CrownIcon,
  Group as HandshakeIcon,
  MonetizationOn as MoneyIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  TrendingDown as UnstakeIcon,
  Assignment as RegisterIcon,
  AttachMoney as ClaimIcon
} from '@mui/icons-material';
import { getFromStorage } from '../utils/storage';
import { getAvoBalance } from '../utils/avoUtils';
import {
  stakeAvo,
  unstakeAvo,
  getStakingInfo,
  getValidators,
  claimRewards,
  registerValidator,
  getStakingStats,
  formatStakingAmount,
  calculateStakingRewards,
  getValidatorStatusInfo,
  getStakingStatus,
  validateStakingAmount,
  getMinimumValidatorStake,
  getMinimumDelegation
} from '../utils/stakingUtils';

const StakingAvo = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState('');
  const [balance, setBalance] = useState('0');
  
  // Staking data
  const [stakingInfo, setStakingInfo] = useState(null);
  const [validators, setValidators] = useState([]);
  const [stakingStats, setStakingStats] = useState(null);
  const [selectedValidator, setSelectedValidator] = useState(null);
  
  // Dialog states
  const [stakeDialogOpen, setStakeDialogOpen] = useState(false);
  const [unstakeDialogOpen, setUnstakeDialogOpen] = useState(false);
  const [validatorDialogOpen, setValidatorDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  
  // Form states
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [validatorRegistration, setValidatorRegistration] = useState({
    name: '',
    commission: '5',
    minStake: getMinimumValidatorStake()
  });
  
  // UI states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const navigate = useNavigate();

  // Initialize component
  useEffect(() => {
    initializeStaking();
  }, []);

  const initializeStaking = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get user address
      const storedWallet = getFromStorage('wallet');
      if (storedWallet && storedWallet.address) {
        setAddress(storedWallet.address);
        
        // Get balance
        const avoBalance = await getAvoBalance(storedWallet.address);
        setBalance(avoBalance);
      }
      
      // Load all staking data
      await Promise.all([
        loadStakingInfo(),
        loadValidators(),
        loadStakingStats()
      ]);
      
    } catch (err) {
      console.error('Error initializing staking:', err);
      setError('Failed to load staking data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const loadStakingInfo = async () => {
    try {
      if (address) {
        const stakingData = await getStakingInfo(address);
        // Ensure we always have a valid stakingInfo object
        setStakingInfo(stakingData || {
          total_staked: '0',
          available_to_unstake: '0',
          pending_rewards: '0',
          validator_status: 'none',
          delegations: [],
          commission: '0',
          delegator_count: 0
        });
      }
    } catch (err) {
      console.error('Error loading staking info:', err);
      // Set default empty state on error
      setStakingInfo({
        total_staked: '0',
        available_to_unstake: '0',
        pending_rewards: '0',
        validator_status: 'none',
        delegations: [],
        commission: '0',
        delegator_count: 0
      });
    }
  };

  const loadValidators = async () => {
    try {
      const validatorsData = await getValidators();
      setValidators(validatorsData);
    } catch (err) {
      console.error('Error loading validators:', err);
    }
  };

  const loadStakingStats = async () => {
    try {
      const statsData = await getStakingStats();
      setStakingStats(statsData);
    } catch (err) {
      console.error('Error loading staking stats:', err);
    }
  };

  const handleRefresh = async () => {
    await initializeStaking();
    setSuccess('Staking data refreshed successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleStake = async () => {
    const validation = validateStakingAmount(
      stakeAmount, 
      balance, 
      selectedValidator ? getMinimumDelegation() : getMinimumValidatorStake()
    );

    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      await stakeAvo(address, stakeAmount, selectedValidator?.address);
      
      setSuccess(`Successfully staked ${stakeAmount} AVO!`);
      setStakeDialogOpen(false);
      setStakeAmount('');
      setSelectedValidator(null);
      
      // Refresh data
      await Promise.all([
        loadStakingInfo(),
        loadStakingStats(),
        getAvoBalance(address).then(setBalance)
      ]);
      
    } catch (err) {
      setError(`Failed to stake: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnstake = async () => {
    const validation = validateStakingAmount(unstakeAmount, stakingInfo?.total_staked || '0');

    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      await unstakeAvo(address, unstakeAmount);
      
      setSuccess(`Successfully unstaked ${unstakeAmount} AVO!`);
      setUnstakeDialogOpen(false);
      setUnstakeAmount('');
      
      // Refresh data
      await Promise.all([
        loadStakingInfo(),
        loadStakingStats(),
        getAvoBalance(address).then(setBalance)
      ]);
      
    } catch (err) {
      setError(`Failed to unstake: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClaimRewards = async () => {
    try {
      setActionLoading(true);
      setError('');
      
      await claimRewards(address);
      
      setSuccess('Rewards claimed successfully!');
      
      // Refresh data
      await Promise.all([
        loadStakingInfo(),
        getAvoBalance(address).then(setBalance)
      ]);
      
    } catch (err) {
      setError(`Failed to claim rewards: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegisterValidator = async () => {
    if (!validatorRegistration.name) {
      setError('Validator name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      await registerValidator(
        address,
        validatorRegistration.name,
        validatorRegistration.commission,
        validatorRegistration.minStake
      );
      
      setSuccess('Successfully registered as validator!');
      setRegisterDialogOpen(false);
      setValidatorRegistration({
        name: '',
        commission: '5',
        minStake: getMinimumValidatorStake()
      });
      
      // Refresh data
      await Promise.all([
        loadStakingInfo(),
        loadValidators(),
        loadStakingStats()
      ]);
      
    } catch (err) {
      setError(`Failed to register validator: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const openStakeDialog = (validator = null) => {
    setSelectedValidator(validator);
    setStakeDialogOpen(true);
    setStakeAmount('');
  };

  // Tab panels
  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  // Get staking status
  const stakingStatus = getStakingStatus(stakingInfo);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading AVO Staking...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <StakingIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
            <Box>
              <Typography variant="h4" color="white" fontWeight="bold">
                🥩 AVO Staking
              </Typography>
              <Typography variant="subtitle1" color="white" opacity={0.9}>
                Stake AVO to secure the network and earn rewards
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={<span>{stakingStatus.icon}</span>}
              label={stakingStatus.status}
              sx={{ 
                bgcolor: stakingStatus.color, 
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            <Tooltip title="Refresh staking data">
              <IconButton onClick={handleRefresh} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* User Status */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Address:
              </Typography>
              <Chip 
                label={`${address.slice(0, 6)}...${address.slice(-4)}`}
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Available:
              </Typography>
              <Chip 
                label={`${formatStakingAmount(balance)} AVO`}
                color="primary"
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Staked:
              </Typography>
              <Chip 
                label={`${formatStakingAmount(stakingInfo?.total_staked || '0')} AVO`}
                color="secondary"
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Rewards:
              </Typography>
              <Chip 
                label={`${formatStakingAmount(stakingInfo?.pending_rewards || '0')} AVO`}
                color="success"
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" mb={2}>Quick Actions</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openStakeDialog()}
              disabled={parseFloat(balance) < parseFloat(getMinimumDelegation())}
            >
              Stake AVO
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<UnstakeIcon />}
              onClick={() => setUnstakeDialogOpen(true)}
              disabled={parseFloat(stakingInfo?.total_staked || '0') === 0}
            >
              Unstake
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<ClaimIcon />}
              onClick={handleClaimRewards}
              disabled={parseFloat(stakingInfo?.pending_rewards || '0') === 0}
            >
              Claim Rewards
            </Button>
          </Grid>
          <Grid item xs={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<RegisterIcon />}
              onClick={() => setRegisterDialogOpen(true)}
              disabled={parseFloat(balance) < parseFloat(getMinimumValidatorStake())}
            >
              Become Validator
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Navigation Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <AppBar position="static" color="transparent" elevation={0}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab icon={<RewardsIcon />} label="My Staking" />
            <Tab icon={<ValidatorIcon />} label="Validators" />
            <Tab icon={<StatsIcon />} label="Statistics" />
          </Tabs>
        </AppBar>

        {/* My Staking Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Staking Overview */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary" mb={2}>
                    💰 Staking Overview
                  </Typography>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">Total Staked:</Typography>
                      <Typography fontWeight="bold">
                        {formatStakingAmount(stakingInfo?.total_staked || '0')} AVO
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">Pending Rewards:</Typography>
                      <Typography fontWeight="bold" color="success.main">
                        {formatStakingAmount(stakingInfo?.pending_rewards || '0')} AVO
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">Est. Annual Rewards:</Typography>
                      <Typography fontWeight="bold">
                        {calculateStakingRewards(
                          stakingInfo?.total_staked || '0',
                          stakingStats?.staking_apr || '12.5'
                        )} AVO
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography color="textSecondary">Status:</Typography>
                      <Chip
                        icon={<span>{stakingStatus.icon}</span>}
                        label={stakingStatus.status}
                        size="small"
                        sx={{ bgcolor: stakingStatus.color, color: 'white' }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Validator Status */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="secondary" mb={2}>
                    👑 Validator Status
                  </Typography>
                  <Stack spacing={2}>
                    {stakingInfo?.validator_status && stakingInfo.validator_status !== 'none' ? (
                      <>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Status:</Typography>
                          <Chip
                            {...getValidatorStatusInfo(stakingInfo.validator_status)}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Commission:</Typography>
                          <Typography fontWeight="bold">
                            {stakingInfo.commission || '0'}%
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Delegators:</Typography>
                          <Typography fontWeight="bold">
                            {stakingInfo.delegators || 0}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Box textAlign="center" py={2}>
                        <CrownIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                        <Typography color="textSecondary">
                          Not a validator
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Minimum {formatStakingAmount(getMinimumValidatorStake())} AVO required
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Delegations */}
            {stakingInfo?.delegations && stakingInfo.delegations.length > 0 && (
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      🤝 My Delegations
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Validator</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Rewards</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stakingInfo.delegations.map((delegation, index) => (
                            <TableRow key={index}>
                              <TableCell>{delegation.validator}</TableCell>
                              <TableCell>{formatStakingAmount(delegation.amount)} AVO</TableCell>
                              <TableCell>{formatStakingAmount(delegation.rewards)} AVO</TableCell>
                              <TableCell>
                                <Button size="small" variant="outlined">
                                  Manage
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Validators Tab */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" mb={2}>Active Validators</Typography>
          <Grid container spacing={2}>
            {validators.map((validator, index) => {
              const statusInfo = getValidatorStatusInfo(validator.status);
              return (
                <Grid item xs={12} md={6} lg={4} key={validator.address || index}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: statusInfo.color, mr: 2 }}>
                          {validator.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{validator.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`${validator.address.slice(0, 6)}...${validator.address.slice(-4)}`}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Stack spacing={1} mb={2}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Total Stake:</Typography>
                          <Typography fontWeight="bold">
                            {formatStakingAmount(validator.total_stake)} AVO
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Commission:</Typography>
                          <Typography fontWeight="bold">{validator.commission}%</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Uptime:</Typography>
                          <Typography fontWeight="bold" color="success.main">
                            {validator.uptime}%
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="textSecondary">Delegators:</Typography>
                          <Typography fontWeight="bold">{validator.delegators}</Typography>
                        </Box>
                      </Stack>

                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<HandshakeIcon />}
                          onClick={() => openStakeDialog(validator)}
                          disabled={validator.status !== 'active'}
                        >
                          Delegate
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<InfoIcon />}
                          onClick={() => setValidatorDialogOpen(true)}
                        >
                          Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>

        {/* Statistics Tab */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" mb={2}>Network Staking Statistics</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    💎 Total Value Staked
                  </Typography>
                  <Typography variant="h4">
                    {formatStakingAmount(stakingStats?.total_staked || '0')} AVO
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Network security backing
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="secondary">
                    📈 Staking APR
                  </Typography>
                  <Typography variant="h4">
                    {stakingStats?.staking_apr || '12.5'}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Annual percentage rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="info">
                    👑 Active Validators
                  </Typography>
                  <Typography variant="h4">
                    {stakingStats?.active_validators || 0} / {stakingStats?.total_validators || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Network validators
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" color="success">
                    🎁 Rewards Distributed
                  </Typography>
                  <Typography variant="h4">
                    {formatStakingAmount(stakingStats?.total_rewards_distributed || '0')} AVO
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total rewards paid
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Stake Dialog */}
      <Dialog open={stakeDialogOpen} onClose={() => setStakeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          🥩 Stake AVO
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {selectedValidator && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Delegating to: <strong>{selectedValidator.name}</strong>
              </Alert>
            )}
            <TextField
              fullWidth
              label="Amount to Stake"
              value={stakeAmount}
              onChange={(e) => setStakeAmount(e.target.value)}
              type="number"
              sx={{ mb: 2 }}
              helperText={`Available: ${formatStakingAmount(balance)} AVO | Min: ${
                selectedValidator ? getMinimumDelegation() : getMinimumValidatorStake()
              } AVO`}
            />
            {stakeAmount && (
              <Box>
                <Typography variant="body2" color="textSecondary" mb={1}>
                  Estimated Annual Rewards:
                </Typography>
                <Typography variant="h6" color="success.main">
                  {calculateStakingRewards(stakeAmount, stakingStats?.staking_apr || '12.5')} AVO
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStakeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleStake} 
            variant="contained"
            disabled={actionLoading || !stakeAmount}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Stake AVO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unstake Dialog */}
      <Dialog open={unstakeDialogOpen} onClose={() => setUnstakeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          📤 Unstake AVO
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Amount to Unstake"
              value={unstakeAmount}
              onChange={(e) => setUnstakeAmount(e.target.value)}
              type="number"
              sx={{ mb: 2 }}
              helperText={`Staked: ${formatStakingAmount(stakingInfo?.total_staked || '0')} AVO`}
            />
            <Alert severity="warning">
              <strong>Note:</strong> Unstaking may have a cooldown period before tokens are available.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnstakeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUnstake} 
            variant="contained"
            disabled={actionLoading || !unstakeAmount}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Unstake AVO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register Validator Dialog */}
      <Dialog open={registerDialogOpen} onClose={() => setRegisterDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          👑 Register as Validator
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Validator Name"
              value={validatorRegistration.name}
              onChange={(e) => setValidatorRegistration({...validatorRegistration, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Commission Rate (%)"
              value={validatorRegistration.commission}
              onChange={(e) => setValidatorRegistration({...validatorRegistration, commission: e.target.value})}
              type="number"
              sx={{ mb: 2 }}
              helperText="Commission rate for delegator rewards (0-100%)"
            />
            <TextField
              fullWidth
              label="Minimum Stake"
              value={validatorRegistration.minStake}
              onChange={(e) => setValidatorRegistration({...validatorRegistration, minStake: e.target.value})}
              type="number"
              sx={{ mb: 2 }}
              helperText={`Minimum: ${formatStakingAmount(getMinimumValidatorStake())} AVO`}
            />
            <Alert severity="info">
              <strong>Requirements:</strong>
              <ul>
                <li>Minimum {formatStakingAmount(getMinimumValidatorStake())} AVO stake</li>
                <li>Reliable server infrastructure</li>
                <li>Technical knowledge of validator operations</li>
              </ul>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRegisterValidator} 
            variant="contained"
            disabled={actionLoading || !validatorRegistration.name}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Register Validator'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StakingAvo;